import { X } from 'lucide-react';
import { ReactNode, useEffect, useRef } from 'react';
import { create } from 'zustand';

export type SnackBarCloseReason = 'timeout' | 'dismiss' | 'clickaway';

export type SnackBarOptions = {
  message?: ReactNode;
  action?: ReactNode;
  autoHideDuration?: number;
  onClose?: (reason: SnackBarCloseReason) => void;
};

const useState = create(() => ({
  props: null as SnackBarOptions | null,
}));

/**
 * Show a global snack bar (pop-up at the top of the screen).
 *
 *   toast('Saved')
 *   toast({ message: 'Saved', autoHideDuration: 5000 })
 *   toast(null) // dismiss
 */
export function toast(message: string): void;
export function toast(opts: SnackBarOptions | null): void;
export function toast(arg: string | SnackBarOptions | null): void {
  useState.setState({
    props: typeof arg === 'string' ? { message: arg } : arg,
  });
}

export const SnackBarGlobalComponent = () => {
  const props = useState(state => state.props);
  const snackBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!props) {
      return;
    }
    const close = (reason: SnackBarCloseReason) => {
      props.onClose?.(reason);
      useState.setState(state => (state.props === props ? { props: null } : state));
    };
    const timeout = window.setTimeout(() => close('timeout'), props.autoHideDuration ?? 2500);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close('dismiss');
      }
    };
    const onClickAway = (event: MouseEvent) => {
      if (!snackBarRef.current?.contains(event.target as Node)) {
        close('clickaway');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    // Deferred so the click that opened the snack bar does not immediately close it.
    const clickAwayTimeout = window.setTimeout(() => document.addEventListener('click', onClickAway));
    return () => {
      window.clearTimeout(timeout);
      window.clearTimeout(clickAwayTimeout);
      window.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onClickAway);
    };
  }, [props]);

  if (!props) {
    return null;
  }
  return (
    <div className="pointer-events-none fixed inset-x-0 top-1.5 z-tooltip flex justify-center px-4">
      <div
        ref={snackBarRef}
        role="status"
        aria-live="polite"
        className="pointer-events-auto flex min-w-72 max-w-xl items-center gap-3 rounded-auditor bg-ink px-4 py-2 text-sm text-ink-inverse shadow-auditor-overlay"
      >
        <div className="flex-1 py-1">{props.message}</div>
        {props.action}
        <button
          type="button"
          aria-label="Dismiss notification"
          className="inline-flex size-6 items-center justify-center rounded-auditor opacity-70 hover:opacity-100"
          onClick={() => {
            props.onClose?.('dismiss');
            useState.setState({ props: null });
          }}
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
};
